import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import {
  useApprovals,
  type ApprovalRegulator, type ApprovalStatus, type ApprovalSubjectKind,
} from '@/lib/queries';

/**
 * /group/approvals (Page 73/N) — every regulator approval on colleges,
 * universities, programmes and specialisations, in one place. Unified
 * Axis-4 collection replaces the old scattered fields on College /
 * Program / Specialization. Cited by NIRF and NAAC.
 */

const STATUS_LABEL: Record<ApprovalStatus, string> = {
  active: 'Active', expired: 'Expired', revoked: 'Revoked',
  superseded: 'Superseded', pending: 'Pending',
};

function statusTone(s: ApprovalStatus): string {
  if (s === 'active') return 'badge-success';
  if (s === 'pending') return 'badge-warning';
  if (s === 'revoked') return 'badge-danger';
  return '';
}

function fmt(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export function GroupApprovalsPage() {
  const nav = useNavigate();
  const [regulator, setRegulator] = useState<ApprovalRegulator | ''>('');
  const [status, setStatus] = useState<ApprovalStatus | ''>('active');
  const [subjectKind, setSubjectKind] = useState<ApprovalSubjectKind | ''>('');
  const [q, setQ] = useState('');

  const list = useApprovals({ regulator, status, subjectKind, q, sortBy: 'validFrom', sortDir: 'desc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const summary = list.data?.summary;

  const byRegulator = summary?.byRegulator ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Compliance · Axis-4</p>
          <h1 className="h-page">Regulator approvals</h1>
          <p className="subhead">
            Every AICTE / UGC / NAAC / NBA / NIRF / PCI / BCI approval on any of your
            colleges, programmes, or branches — with number, validity window, and status.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-5">
        <div className="card kpi"><span className="kpi-label">AICTE</span>
          <span className="kpi-value">{byRegulator.AICTE ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">UGC</span>
          <span className="kpi-value">{byRegulator.UGC ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">NAAC</span>
          <span className="kpi-value">{byRegulator.NAAC ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">NBA</span>
          <span className="kpi-value">{byRegulator.NBA ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">NIRF</span>
          <span className="kpi-value">{byRegulator.NIRF ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search number / title / grantee" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={regulator} onChange={(e) => setRegulator(e.target.value as ApprovalRegulator | '')}>
            <option value="">Any regulator</option>
            {['AICTE', 'UGC', 'NAAC', 'NBA', 'NIRF', 'PCI', 'BCI', 'MCI', 'NCTE', 'INC', 'Other'].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as ApprovalStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[ApprovalStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={subjectKind} onChange={(e) => setSubjectKind(e.target.value as ApprovalSubjectKind | '')}>
            <option value="">Any subject</option>
            <option value="College">College</option>
            <option value="University">University</option>
            <option value="Program">Programme</option>
            <option value="Specialization">Branch</option>
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No approvals in scope"
          emptyHint="Widen the filters, or record a new approval when a regulator letter comes in."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Title / grantee</th>
                  <th>Regulator</th>
                  <th>Subject</th>
                  <th>Window</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r._id}
                    onClick={() => nav(`/group/approvals/${r._id}`)}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') nav(`/group/approvals/${r._id}`); }}
                  >
                    <td className="cell-sub">{r.approvalNumber ?? '—'}</td>
                    <td>
                      <div className="cell-title">{r.title ?? '—'}</div>
                      {r.grantedTo && <div className="cell-sub">{r.grantedTo}</div>}
                    </td>
                    <td className="cell-sub">{r.regulator}</td>
                    <td className="cell-sub">{r.subject?.kind ?? '—'}</td>
                    <td className="cell-sub">{fmt(r.validFrom)} → {r.validTo ? fmt(r.validTo) : 'open'}</td>
                    <td><span className={`badge ${statusTone(r.status as ApprovalStatus)}`}>{STATUS_LABEL[r.status as ApprovalStatus] ?? r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>One row per regulator per subject.</b> When a new AICTE letter arrives, the
        old row is superseded (never deleted). Revocations set status=revoked with a
        required reason.
      </Notice>
    </div>
  );
}
